import React from 'react';

import { UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { disallowedVisitorTranslationMap, spaceTypeTranslationMap } from 'src/features/spaces/utils/constants';
import { SpaceFormSchemaValues } from 'src/features/spaces/utils/formSchema';
import { spacesMessages } from 'src/features/spaces/utils/messages';
import { DISALLOW_USAGE_FOR, SPACE_TYPE } from 'src/features/spaces/utils/types';
import { useListZones } from 'src/features/zone/api/useListZones';
import { useParking } from 'src/providers/ParkingProvider';
import InputFormField from 'src/registry/components/forms/InputFormField';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { Form } from 'src/registry/ui/Form';
import { translate } from 'src/utils/translate';

type SpaceFormProps = {
    form: UseFormReturn<SpaceFormSchemaValues>;
    onSubmit: (values: SpaceFormSchemaValues) => void;
};

function SpaceForm({ onSubmit, form }: SpaceFormProps) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const { data: zonesData } = useListZones({ parkingId });
    const options = React.useMemo(() => {
        if (!zonesData) return [];
        return zonesData.results.map(({ translations, id }) => ({
            label: translate(translations, locale)?.name || '',
            value: id,
        }));
    }, [locale, zonesData]);

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <InputFormField<SpaceFormSchemaValues>
                form={form}
                name="number"
                label={formatMessage(spacesMessages.number)}
            />
            <SelectFormField<SpaceFormSchemaValues>
                form={form}
                name="zone"
                label={formatMessage(spacesMessages.zone)}
                options={options}
            />
            <SelectFormField<SpaceFormSchemaValues>
                form={form}
                name="type"
                label={formatMessage(spacesMessages.type)}
                options={Object.values(SPACE_TYPE)}
                optionTranslationMap={spaceTypeTranslationMap}
            />
            <SelectFormField<SpaceFormSchemaValues>
                form={form}
                name="disallow_usage_for"
                label={formatMessage(spacesMessages.disallowedVisitorCategories)}
                options={Object.values(DISALLOW_USAGE_FOR)}
                optionTranslationMap={disallowedVisitorTranslationMap}
            />
        </Form>
    );
}

export default SpaceForm;
